// src/services/adminLiveVideoService.ts - Admin panel live video management
import unifiedAuth from '../utils/unifiedAuth';
import { config } from '../config';

const API_BASE_URL = config.apiBaseUrl;

interface AdminLiveVideoStats {
  totalLiveVideos: number;
  currentlyLive: number;
  upcomingVideos: number;
  pastVideos: number;
  totalViews: number;
  totalCurrentViewers: number;
  avgViewsPerVideo: number;
  topPerformingVideo?: {
    id: string;
    title: string;
    viewCount: number;
    currentViewers?: number;
  };
}

interface LiveVideoAnalytics {
  videoId: string;
  title: string;
  status: 'LIVE' | 'SCHEDULED' | 'ENDED' | 'CANCELLED';
  viewCount: number;
  currentViewers: number;
  likeCount: number;
  commentCount: number;
  peakViewers: number;
  duration?: number;
  startTime: string;
  endTime?: string;
  engagementRate: number;
}

class AdminLiveVideoService {
  private getAuthHeaders() {
    // Authorization is attached by unifiedAuth.fetch, which refreshes on expiry.
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  }

  /**
   * Get comprehensive live video statistics for admin dashboard
   */
  async getLiveVideoStats(): Promise<AdminLiveVideoStats> {
    try {
      const [currentResponse, upcomingResponse, pastResponse] = await Promise.all([
        unifiedAuth.fetch(`${API_BASE_URL}/live-video/current`, { 
          headers: this.getAuthHeaders() 
        }).catch(() => null),
        unifiedAuth.fetch(`${API_BASE_URL}/live-video/upcoming?page=0&size=50`, { 
          headers: this.getAuthHeaders() 
        }).catch(() => null),
        unifiedAuth.fetch(`${API_BASE_URL}/live-video/past?page=0&size=100`, { 
          headers: this.getAuthHeaders() 
        }).catch(() => null)
      ]);

      let totalLiveVideos = 0;
      let currentlyLive = 0;
      let upcomingVideos = 0;
      let pastVideos = 0;
      let totalViews = 0;
      let totalCurrentViewers = 0;
      let topPerformingVideo: { id: string; title: string; viewCount: number; currentViewers?: number } | undefined = undefined;

      // Process current live video
      if (currentResponse?.ok) {
        const currentData = await currentResponse.json();
        if (currentData.data) {
          currentlyLive = 1;
          totalCurrentViewers = currentData.data.currentViewers || 0;
          totalViews += currentData.data.viewCount || 0;
          
          topPerformingVideo = {
            id: currentData.data.id,
            title: currentData.data.title,
            viewCount: currentData.data.viewCount || 0,
            currentViewers: currentData.data.currentViewers || 0
          };
        }
      }

      // Process upcoming videos
      if (upcomingResponse?.ok) {
        const upcomingData = await upcomingResponse.json();
        if (upcomingData.data?.content) {
          upcomingVideos = upcomingData.data.totalElements || upcomingData.data.content.length;
        }
      }

      // Process past videos
      if (pastResponse?.ok) {
        const pastData = await pastResponse.json();
        if (pastData.data?.content) {
          pastVideos = pastData.data.totalElements || pastData.data.content.length;
          
          // Calculate total views from past videos
          pastData.data.content.forEach((video: { id: string; title: string; viewCount?: number }) => {
            totalViews += video.viewCount || 0;
            
            // Update top performing video if this one has more views
            if (!topPerformingVideo || (video.viewCount || 0) > topPerformingVideo.viewCount) {
              topPerformingVideo = {
                id: video.id,
                title: video.title,
                viewCount: video.viewCount || 0
              };
            }
          });
        }
      }

      totalLiveVideos = currentlyLive + upcomingVideos + pastVideos;
      const avgViewsPerVideo = totalLiveVideos > 0 ? Math.round(totalViews / totalLiveVideos) : 0;

      return {
        totalLiveVideos,
        currentlyLive,
        upcomingVideos,
        pastVideos,
        totalViews,
        totalCurrentViewers,
        avgViewsPerVideo,
        topPerformingVideo
      };
    } catch (error) {
      console.error('Failed to fetch live video stats:', error);
      return {
        totalLiveVideos: 0,
        currentlyLive: 0,
        upcomingVideos: 0,
        pastVideos: 0,
        totalViews: 0,
        totalCurrentViewers: 0,
        avgViewsPerVideo: 0
      };
    }
  }

  /**
   * Get detailed analytics for all live videos
   */
  async getLiveVideoAnalytics(): Promise<LiveVideoAnalytics[]> {
    try {
      const analytics: LiveVideoAnalytics[] = [];

      // Get current live video analytics
      try {
        const currentResponse = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/current`, {
          headers: this.getAuthHeaders()
        });
        
        if (currentResponse.ok) {
          const currentData = await currentResponse.json();
          if (currentData.data) {
            const video = currentData.data;
            
            // Get detailed stats for current video
            const statsResponse = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/${video.id}/stats`, {
              headers: this.getAuthHeaders()
            });
            
            let stats = null;
            if (statsResponse.ok) {
              const statsData = await statsResponse.json();
              stats = statsData.data;
            }

            analytics.push({
              videoId: video.id,
              title: video.title,
              status: 'LIVE',
              viewCount: stats?.viewCount || video.viewCount || 0,
              currentViewers: stats?.currentViewers || video.currentViewers || 0,
              likeCount: stats?.likeCount || video.likeCount || 0,
              commentCount: stats?.commentCount || 0,
              peakViewers: video.maxViewers || stats?.currentViewers || 0,
              startTime: video.actualStartTime || video.createdAt,
              engagementRate: this.calculateEngagementRate(
                stats?.viewCount || 0,
                stats?.likeCount || 0,
                stats?.commentCount || 0
              )
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch current live video analytics:', error);
      }

      // Get past videos analytics (limited to recent ones for performance)
      try {
        const pastResponse = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/past?page=0&size=20`, {
          headers: this.getAuthHeaders()
        });
        
        if (pastResponse.ok) {
          const pastData = await pastResponse.json();
          if (pastData.data?.content) {
            for (const video of pastData.data.content) {
              analytics.push({
                videoId: video.id,
                title: video.title,
                status: video.liveStatus || 'ENDED',
                viewCount: video.viewCount || 0,
                currentViewers: 0,
                likeCount: video.likeCount || 0,
                commentCount: 0,
                peakViewers: video.maxViewers || 0,
                startTime: video.actualStartTime || video.createdAt,
                endTime: video.endTime,
                duration: video.durationSeconds,
                engagementRate: this.calculateEngagementRate(
                  video.viewCount || 0,
                  video.likeCount || 0,
                  0
                )
              });
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch past live videos analytics:', error);
      }

      return analytics;
    } catch (error) {
      console.error('Failed to fetch live video analytics:', error);
      return [];
    }
  }

  /**
   * Get view trends for live videos over time
   */
  async getLiveVideoViewTrends(days = 30): Promise<{
    date: string;
    views: number;
    liveVideos: number;
    avgViewersPerVideo: number;
  }[]> {
    try {
      // This would typically come from an analytics endpoint
      // For now, we'll simulate based on past videos data
      const pastResponse = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/past?page=0&size=100`, {
        headers: this.getAuthHeaders()
      });

      if (!pastResponse.ok) {
        return [];
      }

      const pastData = await pastResponse.json();
      const trends: Map<string, { views: number; count: number }> = new Map();

      if (pastData.data?.content) {
        pastData.data.content.forEach((video: { createdAt?: string; actualStartTime?: string; viewCount?: number }) => {
          const dateStr = video.createdAt || video.actualStartTime;
          if (!dateStr) return;
          const date = new Date(dateStr).toISOString().split('T')[0];
          const existing = trends.get(date) || { views: 0, count: 0 };
          existing.views += video.viewCount || 0;
          existing.count += 1;
          trends.set(date, existing);
        });
      }

      return Array.from(trends.entries())
        .map(([date, data]) => ({
          date,
          views: data.views,
          liveVideos: data.count,
          avgViewersPerVideo: data.count > 0 ? Math.round(data.views / data.count) : 0
        }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .slice(-days);
    } catch (error) {
      console.error('Failed to fetch live video view trends:', error);
      return [];
    }
  }

  /**
   * Update live video status (for admin controls)
   */
  async updateLiveVideoStatus(videoId: string, status: 'LIVE' | 'ENDED' | 'CANCELLED'): Promise<boolean> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/admin/live-video/${videoId}/status`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ status })
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to update live video status:', error);
      return false;
    }
  }

  private calculateEngagementRate(views: number, likes: number, comments: number): number {
    if (views === 0) return 0;
    return ((likes + comments) / views) * 100;
  }
}

export default new AdminLiveVideoService();
export type { AdminLiveVideoStats, LiveVideoAnalytics };